import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import {
  TennisBall,
  ArrowRight,
  TennisBall as TennisBallIcon,
  UsersThree,
  Buildings,
  EnvelopeSimple,
  Link as LinkIcon,
} from "@phosphor-icons/react";

const DEMO = [
  { label: "Owner", email: "owner@courtside.app", pw: "Owner123!" },
  { label: "Admin", email: "head@demo-club.co.uk", pw: "Head123!" },
  { label: "Coach", email: "coach@demo-club.co.uk", pw: "Coach123!" },
  { label: "Parent", email: "parent@demo-club.co.uk", pw: "Parent123!" },
];

const CONTACT_EMAIL = "hello@courtside.app";

export default function Login() {
  const { login } = useAuth();
  const [params] = useSearchParams();
  const showDemo = params.has("demo");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const openInvite = (e) => {
    e.preventDefault();
    const trimmed = (inviteCode || "").trim();
    if (!trimmed) return;
    // Accept either a full URL or just the token portion
    const token = trimmed.includes("/invite/") ? trimmed.split("/invite/")[1].split(/[?#]/)[0] : trimmed;
    window.location.href = `/invite/${token}`;
  };

  return (
    <div className="auth-bg min-h-screen flex items-center justify-center px-4 py-10" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-md bg-[#dfff00] flex items-center justify-center">
            <TennisBall size={22} weight="duotone" color="#09090b" />
          </div>
          <div>
            <div className="font-heading uppercase tracking-tight text-2xl leading-none flex items-baseline">
              <span className="text-white">courtside</span><span className="text-[#dfff00] ml-0.5">OS</span>
            </div>
            <div className="font-label mt-1">tennis coaching portal</div>
          </div>
        </div>

        <div className="surface p-7">
          <h1 className="font-heading uppercase text-3xl mb-1">Sign in</h1>
          <p className="text-zinc-400 text-sm mb-6">
            Use the credentials your club sent you.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="font-label block mb-1.5">Email</label>
              <input type="email" required className="input" value={email} onChange={(e) => setEmail(e.target.value.replace(/\s+/g, ""))} data-testid="login-email" autoComplete="email" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="font-label">Password</label>
                <a href="/forgot-password" className="text-[11px] text-zinc-500 hover:text-[#dfff00] uppercase tracking-wider font-semibold" data-testid="forgot-password-link">
                  Forgot password?
                </a>
              </div>
              <input type="password" required className="input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" />
            </div>
            {err && <div className="text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-3" data-testid="login-error">{err}</div>}
            <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="login-submit">
              {busy ? "Signing in…" : (<>Sign in <ArrowRight size={18} weight="bold" /></>)}
            </button>
          </form>
        </div>

        {/* Signposts */}
        <div className="mt-7 space-y-2.5" data-testid="signposts">
          <Signpost
            icon={TennisBallIcon}
            label="Coach or head coach?"
            body="Use the credentials your club sent you above."
            tone="yellow"
            testid="signpost-coach"
          />
          <Signpost
            icon={UsersThree}
            label="Parent?"
            body="Check your inbox or WhatsApp for an activation link from your child's club."
            tone="green"
            testid="signpost-parent"
            action={
              <button
                onClick={() => setInviteOpen((v) => !v)}
                className="btn-ghost text-xs mt-2 -ml-1.5"
                data-testid="open-invite-form"
              >
                <LinkIcon size={13} weight="bold" /> I have an invite link
              </button>
            }
            expanded={inviteOpen && (
              <form onSubmit={openInvite} className="mt-2 flex gap-2" data-testid="invite-paste-form">
                <input
                  className="input"
                  style={{ height: "2.5rem", fontSize: "0.85rem" }}
                  placeholder="Paste your invite link or token"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  data-testid="invite-paste-input"
                />
                <button type="submit" className="btn-primary" style={{ height: "2.5rem", padding: "0 1rem" }} data-testid="invite-go">
                  Go
                </button>
              </form>
            )}
          />
          <Signpost
            icon={Buildings}
            label="Run a tennis club?"
            body="courtsideOS partners with clubs directly — onboarded one at a time so every club gets hands-on setup."
            tone="terracotta"
            testid="signpost-owner"
            action={
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Bringing my club to courtsideOS")}`}
                className="btn-ghost text-xs mt-2 -ml-1.5 inline-flex items-center gap-1.5"
                data-testid="contact-owner"
              >
                <EnvelopeSimple size={13} weight="bold" /> Get in touch
              </a>
            }
          />
        </div>

        {showDemo && (
          <div className="mt-6 surface p-4" data-testid="demo-accounts">
            <div className="font-label mb-2.5">Demo accounts (preview only)</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  onClick={() => { setEmail(d.email); setPassword(d.pw); }}
                  className="bg-[#09090b] border border-[#27272a] rounded-md text-left px-3 py-2 hover:border-[#dfff00]/40 transition-colors"
                  data-testid={`demo-${d.label.toLowerCase().replace(" ", "-")}`}
                >
                  <div className="font-label">{d.label}</div>
                  <div className="text-xs text-zinc-300 mt-0.5 truncate">{d.email}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Signpost({ icon: Icon, label, body, tone, action, expanded, testid }) {
  const toneStyles = {
    yellow: { color: "#dfff00", bg: "rgba(223, 255, 0, 0.08)", border: "rgba(223, 255, 0, 0.25)" },
    green: { color: "#16ec06", bg: "rgba(22, 236, 6, 0.06)", border: "rgba(22, 236, 6, 0.22)" },
    terracotta: { color: "#e3735e", bg: "rgba(227, 115, 94, 0.06)", border: "rgba(227, 115, 94, 0.22)" },
  };
  const t = toneStyles[tone] || toneStyles.yellow;
  return (
    <div
      className="rounded-md p-3.5 border"
      style={{ background: t.bg, borderColor: t.border }}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
          style={{ background: t.color, color: "#09090b" }}
        >
          <Icon size={16} weight="bold" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold" style={{ color: t.color }}>{label}</div>
          <div className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{body}</div>
          {action}
          {expanded}
        </div>
      </div>
    </div>
  );
}
